import * as React from 'react';
import isEqual from 'lodash/isEqual';
import difference from 'lodash/difference';
import { FieldExtensionSDK } from 'contentful-ui-extensions-sdk';
import { mapSort } from '../../utils';
import { SortableList } from './SortableList';
import { PreviewsFn, Product } from '../../interfaces';
import arrayMove from 'array-move';

interface Props {
  sdk: FieldExtensionSDK;
  disabled: boolean;
  onChange: (skus: string[]) => void;
  skus: string[];
  fetchProductPreviews: PreviewsFn;
  isProduct: boolean;
}

interface State {
  productPreviews: Product[];
}

export class ProductPreviews extends React.Component<Props, State> {
  state = {
    productPreviews: [],
  };

  componentDidMount(): void {
    this.updateProductPreviews();
  }

  componentDidUpdate({ skus: prevSKUs }: Props): void {
    if (!isEqual(this.props.skus, prevSKUs)) {
      const lengthHasChanged = this.props.skus.length !== prevSKUs.length;
      const skusDiffer = difference(this.props.skus, prevSKUs).length > 0;
      const shouldRefetchProducts = lengthHasChanged || skusDiffer;
      this.updateProductPreviews(shouldRefetchProducts);
    }
  }

  updateProductPreviews = async (shouldRefetch = true): Promise<void> => {
    try {
      const { fetchProductPreviews, skus } = this.props;
      const productPreviewsUnsorted = shouldRefetch
        ? await fetchProductPreviews(skus)
        : this.state.productPreviews;
      const productPreviews = mapSort(
        productPreviewsUnsorted,
        skus,
        this.props.isProduct ? 'slug' : 'sku'
      );
      this.setState({ productPreviews });
    } catch {
      this.props.sdk.notifier.error(
        'There was an error fetching the data for the selected products.'
      );
    }
  };

  deleteItem = (index: number): void => {
    const skus = [...this.props.skus];
    skus.splice(index, 1);
    this.props.onChange(skus);
  };

  onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }): void => {
    const skus = arrayMove(this.props.skus, oldIndex, newIndex);
    this.props.onChange(skus);
  };

  render(): JSX.Element {
    return (
      <SortableList
        disabled={this.props.disabled}
        onSortStart={(_, e) => e.preventDefault()}
        onSortEnd={this.onSortEnd}
        axis="xy"
        productPreviews={this.state.productPreviews}
        deleteFn={this.deleteItem}
        useDragHandle
      />
    );
  }
}
